import { useEffect, useRef, useState, type MouseEvent } from "react";
import { Bell } from "lucide-react";
import { Alert } from "@/components/alert";
import { Button } from "@/components/ui/button";
import { NotificationsEmpty } from "@/components/notifications-empty";
import { toast } from "@/components/toaster";
import { useNotifications } from "@/hooks/use-notifications";
import { useDismissOnOutsideClick } from "@/hooks/use-dismiss-on-outside-click";
import { useI18n } from "@/lib/i18n";
import { pathForItem } from "@/lib/navigation";
import { useRouteNavigate } from "@/lib/routing";
import { trackNotifications } from "@/features/notifications/analytics";
import { renderNotification } from "@/features/notifications/renderers";
import { NotificationItem } from "@/components/notification-item";
import "@/components/notification-bell.css";

export const BADGE_CAP = 9;

export function NotificationBell() {
  const { t, locale, formatNumber } = useI18n();
  const navigate = useRouteNavigate();
  const inbox = useNotifications();
  const [open, setOpen] = useState(false);
  const [actionFailed, setActionFailed] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const lastUnread = useRef<number | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const root = useDismissOnOutsideClick<HTMLDivElement>(open, () =>
    setOpen(false),
  );

  useEffect(() => {
    if (!inbox.unreadKnown) return;
    const previous = lastUnread.current;
    lastUnread.current = inbox.unread;
    if (previous !== null && inbox.unread > previous)
      setAnnouncement(
        t("notifications.bell.labelUnread", { count: inbox.unread }),
      );
  }, [inbox.unread, inbox.unreadKnown, t]);

  // A new Critical or High CVE alert surfaces as a toast; the first load is not new.
  const newestId = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (inbox.loading) return;
    const [newest] = inbox.items;
    const previous = newestId.current;
    newestId.current = newest?.id ?? null;
    if (previous === undefined || !newest || newest.id === previous) return;
    const severe =
      newest.type.startsWith("cve.") &&
      !newest.readAt &&
      ["critical", "high"].includes(String(newest.data.severity));
    if (severe) toast.error(renderNotification(newest, t, locale).title);
  }, [inbox.items, inbox.loading, t, locale]);

  useEffect(() => {
    if (!open) return;
    setActionFailed(false);
    trackNotifications("bell", "open");
    panel.current?.querySelector<HTMLElement>("button, a")?.focus();
  }, [open]);

  const close = (returnFocus = true) => {
    setOpen(false);
    if (returnFocus) button.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  const badge =
    inbox.unread > BADGE_CAP ? `${BADGE_CAP}+` : formatNumber(inbox.unread);
  const label = inbox.unreadKnown
    ? t("notifications.bell.labelUnread", { count: inbox.unread })
    : t("notifications.bell.label");

  const openItem = async (index: number) => {
    const item = inbox.items[index];
    const { href } = renderNotification(item, t, locale);
    setActionFailed(false);
    if (!item.readAt) {
      try {
        await inbox.markRead(item.id);
      } catch {
        setActionFailed(true);
        return;
      }
    }
    close(false);
    void navigate(href);
  };

  // Modified and middle clicks keep the link's native behaviour (new tab).
  const onItemClick = (event: MouseEvent<HTMLAnchorElement>, index: number) => {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    event.preventDefault();
    void openItem(index);
  };

  return (
    <div className="notification-bell" ref={root}>
      <Button
        ref={button}
        variant="ghost"
        size="icon"
        className="notification-bell-button"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((value) => !value)}
      >
        <Bell size={20} aria-hidden="true" />
        {inbox.unread > 0 && (
          <span className="notification-bell-badge" aria-hidden="true">
            {badge}
          </span>
        )}
      </Button>
      <span
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
        data-testid="bell-announcer"
      >
        {announcement}
      </span>
      {open && (
        <div
          className="notification-bell-panel popover-arrow"
          role="dialog"
          aria-label={t("notifications.panel.title")}
          ref={panel}
        >
          <div className="notification-bell-head">
            <span>{t("notifications.panel.title")}</span>
            <Button
              variant="ghost"
              size="inline"
              className="notification-bell-mark-all"
              disabled={inbox.unread === 0 || !inbox.items.length}
              onClick={() => {
                setActionFailed(false);
                inbox.markAllRead().catch(() => setActionFailed(true));
              }}
            >
              {t("notifications.markAllRead")}
            </Button>
          </div>
          {actionFailed && (
            <Alert
              tone="error"
              className="notification-bell-alert"
              title={t("notifications.error.action")}
            />
          )}
          {inbox.error ? (
            <Alert
              tone="error"
              className="notification-bell-alert"
              title={t("notifications.error.load")}
              action={
                <Button
                  variant="ghost"
                  size="inline"
                  onClick={() => void inbox.refresh()}
                >
                  {t("notifications.retry")}
                </Button>
              }
            />
          ) : inbox.loading ? (
            <div className="notification-bell-state" role="status">
              {t("notifications.loading")}
            </div>
          ) : inbox.items.length === 0 ? (
            <NotificationsEmpty title={t("notifications.empty")} />
          ) : (
            <ul className="notification-bell-list">
              {inbox.items.map((item, index) => (
                <li key={item.id}>
                  <NotificationItem
                    item={item}
                    onOpen={(_, event) => onItemClick(event, index)}
                  />
                </li>
              ))}
            </ul>
          )}
          <div className="notification-bell-foot">
            <Button
              variant="ghost"
              className="notification-bell-view-all text-link hover:text-link-hover"
              onClick={() => {
                close(false);
                void navigate(pathForItem("Notifications"));
              }}
            >
              {t("notifications.viewAll")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
